import { create } from 'zustand'

export type VideoJobStatus = 'pending' | 'processing' | 'completed' | 'failed'

export interface VideoJob {
  jobId: string
  projectId: string
  imageId: string
  status: VideoJobStatus
  error?: string
  videoApiPath?: string
  startedAt: number
}

export interface VideoToast {
  id: string
  jobId: string
  projectId: string
  status: 'completed' | 'failed'
  message: string
  createdAt: number
}

// Remotion レンダージョブ（AI動画生成とは別）
export interface RenderJobEntry {
  jobId: string
  projectId: string
  projectName: string
  startedAt: number
  // 進捗追跡用
  templateId?: string
  aspectRatio?: '9:16' | '16:9' | '1:1' | '4:5'
  status?: 'queued' | 'processing' | 'completed' | 'failed'
  progress?: number
  queuePosition?: number | null
  downloadUrl?: string
  error?: string
  autoDownloaded?: boolean
}

export interface RenderToast {
  id: string
  jobId: string
  projectId: string
  projectName: string
  status: 'completed' | 'failed'
  createdAt: number
  downloadUrl?: string
}

// 画像追加生成タスク（シーン追加生成など）
export interface ImageGeneratingTask {
  taskId: string
  label: string
  startedAt: number
}

interface VideoJobStore {
  // AI 動画生成ジョブ
  jobs: Record<string, VideoJob>
  toasts: VideoToast[]
  addJob: (job: VideoJob) => void
  updateJob: (jobId: string, patch: Partial<VideoJob>) => void
  removeJob: (jobId: string) => void
  addToast: (toast: VideoToast) => void
  dismissToast: (id: string) => void
  /** imageId に対して進行中ジョブが存在するか */
  hasActiveJobForImage: (imageId: string) => boolean

  // 画像追加生成タスク（シーン追加生成など）
  imageGeneratingTasks: ImageGeneratingTask[]
  addImageGeneratingTask: (task: ImageGeneratingTask) => void
  removeImageGeneratingTask: (taskId: string) => void

  // 追加シーン生成の進行中 jobId（ページ遷移をまたいで保持）
  // key: `${projectId}-${type}`  value: jobId
  pendingExtraJobIds: Record<string, string>
  setPendingExtraJobId: (key: string, jobId: string) => void
  clearPendingExtraJobId: (key: string) => void

  // Remotion レンダージョブ（バックグラウンド追跡用）
  activeRenderJobs: Record<string, RenderJobEntry>
  isRenderPageMounted: boolean
  renderToasts: RenderToast[]
  addRenderJobEntry: (entry: RenderJobEntry) => void
  updateRenderJobEntry: (jobId: string, patch: Partial<RenderJobEntry>) => void
  removeRenderJobEntry: (jobId: string) => void
  setRenderPageMounted: (mounted: boolean) => void
  addRenderToast: (toast: RenderToast) => void
  dismissRenderToast: (id: string) => void
}

export const useVideoJobStore = create<VideoJobStore>((set, get) => ({
  // AI 動画生成ジョブ
  jobs: {},
  toasts: [],

  addJob: (job) => set(s => ({ jobs: { ...s.jobs, [job.jobId]: job } })),

  updateJob: (jobId, patch) => set(s => ({
    jobs: s.jobs[jobId]
      ? { ...s.jobs, [jobId]: { ...s.jobs[jobId], ...patch } }
      : s.jobs,
  })),

  removeJob: (jobId) => set(s => {
    const { [jobId]: _, ...rest } = s.jobs
    return { jobs: rest }
  }),

  addToast: (toast) => set(s => ({ toasts: [...s.toasts, toast] })),

  dismissToast: (id) => set(s => ({ toasts: s.toasts.filter(t => t.id !== id) })),

  hasActiveJobForImage: (imageId) => {
    return Object.values(get().jobs).some(
      j => j.imageId === imageId && (j.status === 'pending' || j.status === 'processing')
    )
  },

  // 画像追加生成タスク
  imageGeneratingTasks: [],
  addImageGeneratingTask: (task) => set(s => ({
    imageGeneratingTasks: s.imageGeneratingTasks.some(t => t.taskId === task.taskId)
      ? s.imageGeneratingTasks  // 重複追加を防止
      : [...s.imageGeneratingTasks, task],
  })),
  removeImageGeneratingTask: (taskId) => set(s => ({
    imageGeneratingTasks: s.imageGeneratingTasks.filter(t => t.taskId !== taskId),
  })),

  // 追加シーン生成の進行中 jobId
  pendingExtraJobIds: {},
  setPendingExtraJobId: (key, jobId) => set(s => ({
    pendingExtraJobIds: { ...s.pendingExtraJobIds, [key]: jobId },
  })),
  clearPendingExtraJobId: (key) => set(s => {
    const { [key]: _, ...rest } = s.pendingExtraJobIds
    return { pendingExtraJobIds: rest }
  }),

  // Remotion レンダージョブ
  activeRenderJobs: {},
  isRenderPageMounted: false,
  renderToasts: [],

  addRenderJobEntry: (entry) => set(s => ({
    activeRenderJobs: s.activeRenderJobs[entry.jobId]
      ? s.activeRenderJobs
      : { ...s.activeRenderJobs, [entry.jobId]: entry },
  })),

  updateRenderJobEntry: (jobId, patch) => set(s => ({
    activeRenderJobs: s.activeRenderJobs[jobId]
      ? { ...s.activeRenderJobs, [jobId]: { ...s.activeRenderJobs[jobId], ...patch } }
      : s.activeRenderJobs,
  })),

  removeRenderJobEntry: (jobId) => set(s => {
    const { [jobId]: _, ...rest } = s.activeRenderJobs
    return { activeRenderJobs: rest }
  }),

  setRenderPageMounted: (mounted) => set({ isRenderPageMounted: mounted }),

  addRenderToast: (toast) => set(s => ({ renderToasts: [...s.renderToasts, toast] })),

  dismissRenderToast: (id) => set(s => ({ renderToasts: s.renderToasts.filter(t => t.id !== id) })),
}))
